import {
    Group,
    Loader,
    Modal,
    Select,
    Stack,
    Table,
    Text,
    TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { AdminRO } from '@routellers/types/dist/admin/admin.ro';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api, apiErrorMessage } from '../api';

type HourRow = {
    hour: number;
    requestCount: number;
    firstRequestAt: string;
    lastRequestAt: string;
};

type RequestRow = {
    id: number;
    requestTime: string;
    method: string;
    path: string;
};

const formatDateInput = (date: Date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const formatClock = (value: Date | string | null | undefined) => {
    if (!value) return '—';
    return new Date(value).toLocaleTimeString(undefined, {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
    });
};

export const UsersActivity = () => {
    const { t } = useTranslation();
    const [admins, setAdmins] = useState<AdminRO[]>([]);
    const [adminsLoading, setAdminsLoading] = useState(true);
    const [selectedEmail, setSelectedEmail] = useState<string | null>(null);
    const [selectedDate, setSelectedDate] = useState(() => formatDateInput(new Date()));
    const [rows, setRows] = useState<HourRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [requestsOpen, setRequestsOpen] = useState(false);
    const [requestsHour, setRequestsHour] = useState<number | null>(null);
    const [requests, setRequests] = useState<RequestRow[]>([]);
    const [requestsLoading, setRequestsLoading] = useState(false);

    useEffect(() => {
        let cancelled = false;

        (async () => {
            setAdminsLoading(true);
            try {
                const { data } = await api.admins.query(undefined);
                if (!cancelled) setAdmins(data || []);
            } catch (error) {
                if (!cancelled) {
                    notifications.show({ color: 'red', message: apiErrorMessage(error) });
                    setAdmins([]);
                }
            } finally {
                if (!cancelled) setAdminsLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, []);

    const loadActivity = useCallback(async () => {
        if (!selectedEmail || !selectedDate) {
            setRows([]);
            return;
        }

        setLoading(true);
        try {
            const { data } = await api.admins.getAllAdminsActivityByDate({
                adminEmail: selectedEmail,
                date: selectedDate,
            });
            setRows(
                (data || []).map((item) => ({
                    hour: item.hour,
                    requestCount: item.requestCount,
                    firstRequestAt: formatClock(item.firstRequestAt),
                    lastRequestAt: formatClock(item.lastRequestAt),
                })),
            );
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [selectedEmail, selectedDate]);

    useEffect(() => {
        void loadActivity();
    }, [loadActivity]);

    const adminOptions = useMemo(
        () =>
            admins.map((admin) => ({
                value: admin.email,
                label: `${admin.name} (${admin.email})`,
            })),
        [admins],
    );

    const openRequests = async (hour: number) => {
        if (!selectedEmail) return;

        setRequestsHour(hour);
        setRequestsOpen(true);
        setRequestsLoading(true);
        try {
            const { data } = await api.admins.getAllAdminsActivityRequests({
                adminEmail: selectedEmail,
                date: selectedDate,
                hour,
            });
            setRequests(
                (data || []).map((item) => ({
                    id: item.id,
                    requestTime: formatClock(item.requestTime),
                    method: item.method,
                    path: item.path,
                })),
            );
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
            setRequests([]);
        } finally {
            setRequestsLoading(false);
        }
    };

    if (adminsLoading) return <Loader />;

    return (
        <Stack gap='md'>
            <Group gap='md' align='flex-end'>
                <Select
                    label={t('pages.users.activity.selectAdmin')}
                    placeholder={t('pages.users.activity.selectAdmin')}
                    data={adminOptions}
                    value={selectedEmail}
                    onChange={setSelectedEmail}
                    searchable
                    clearable
                    w={320}
                />
                <TextInput
                    type='date'
                    label={t('pages.users.activity.date')}
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.currentTarget.value)}
                    w={180}
                />
            </Group>

            {!selectedEmail ? (
                <Text size='sm' c='dimmed'>
                    {t('pages.users.activity.selectAdminHint')}
                </Text>
            ) : loading ? (
                <Loader />
            ) : (
                <Table.ScrollContainer minWidth={720}>
                    <Table striped highlightOnHover withTableBorder>
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th>{t('pages.users.activity.columns.hour')}</Table.Th>
                                <Table.Th>{t('pages.users.activity.columns.requests')}</Table.Th>
                                <Table.Th>{t('pages.users.activity.columns.firstRequest')}</Table.Th>
                                <Table.Th>{t('pages.users.activity.columns.lastRequest')}</Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {rows.map((row) => (
                                <Table.Tr
                                    key={row.hour}
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => void openRequests(row.hour)}
                                >
                                    <Table.Td>
                                        {row.hour}:00 – {row.hour + 1}:00
                                    </Table.Td>
                                    <Table.Td>{row.requestCount}</Table.Td>
                                    <Table.Td>{row.firstRequestAt}</Table.Td>
                                    <Table.Td>{row.lastRequestAt}</Table.Td>
                                </Table.Tr>
                            ))}
                            {!rows.length && (
                                <Table.Tr>
                                    <Table.Td colSpan={4}>
                                        <Text size='sm' c='dimmed'>
                                            {t('pages.users.activity.empty')}
                                        </Text>
                                    </Table.Td>
                                </Table.Tr>
                            )}
                        </Table.Tbody>
                    </Table>
                </Table.ScrollContainer>
            )}

            <Modal
                opened={requestsOpen}
                onClose={() => setRequestsOpen(false)}
                title={
                    selectedEmail && requestsHour != null
                        ? t('pages.users.activity.requestsTitle', {
                              email: selectedEmail,
                              date: selectedDate,
                              hour: `${requestsHour}:00–${requestsHour + 1}:00`,
                          })
                        : t('pages.users.activity.requests')
                }
                size='lg'
                centered
            >
                {requestsLoading ? (
                    <Loader />
                ) : (
                    <Table.ScrollContainer minWidth={560}>
                        <Table striped withTableBorder>
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th>{t('pages.users.activity.columns.time')}</Table.Th>
                                    <Table.Th>{t('pages.users.activity.columns.method')}</Table.Th>
                                    <Table.Th>{t('pages.users.activity.columns.path')}</Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {requests.map((row) => (
                                    <Table.Tr key={row.id}>
                                        <Table.Td>{row.requestTime}</Table.Td>
                                        <Table.Td>{row.method}</Table.Td>
                                        <Table.Td>
                                            <Text size='sm' style={{ wordBreak: 'break-all' }}>
                                                {row.path}
                                            </Text>
                                        </Table.Td>
                                    </Table.Tr>
                                ))}
                                {!requests.length && (
                                    <Table.Tr>
                                        <Table.Td colSpan={3}>
                                            <Text size='sm' c='dimmed'>
                                                {t('pages.users.activity.emptyRequests')}
                                            </Text>
                                        </Table.Td>
                                    </Table.Tr>
                                )}
                            </Table.Tbody>
                        </Table>
                    </Table.ScrollContainer>
                )}
            </Modal>
        </Stack>
    );
};
